import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import {
  Lightbulb,
  FileText,
  Workflow,
  ArrowRight,
  BookOpen,
  Newspaper,
  Search,
  Layers,
  FolderTree,
  Globe,
  PlayCircle,
  Settings,
  ChevronRight,
  ExternalLink,
  Info,
  Zap,
  Target,
  CheckCircle2,
  AlertCircle,
  Link2,
  Bot,
} from "lucide-react"
import type { ElementType } from "react"

// Chapter/section types
interface DocSection {
  id: string
  title: string
  icon: ElementType
  color: string
  content: React.ReactNode
}

// Reusable components for doc content
function Callout({ type = "info", children }: { type?: "info" | "tip" | "warning", children: React.ReactNode }) {
  const styles = {
    info: "bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300",
    tip: "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-300",
    warning: "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300",
  }
  const icons = {
    info: Info,
    tip: Zap,
    warning: AlertCircle,
  }
  const Icon = icons[type]

  return (
    <div className={cn("flex gap-3 p-4 rounded-lg border", styles[type])}>
      <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div className="text-sm">{children}</div>
    </div>
  )
}

function StepList({ steps }: { steps: { title: string, description: string }[] }) {
  return (
    <div className="space-y-3">
      {steps.map((step, i) => (
        <div key={i} className="flex flex-row items-start gap-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0">
            {i + 1}
          </span>
          <div>
            <span className="font-medium">{step.title}</span>
            <p className="text-sm text-muted-foreground mt-0.5">{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

function FeatureList({ features }: { features: string[] }) {
  return (
    <ul className="space-y-2">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  )
}

// DocLink that closes the modal when clicking links to app pages
function DocLink({ to, children, onNavigate }: { to: string, children: React.ReactNode, onNavigate?: () => void }) {
  const navigate = useNavigate()

  return (
    <button
      onClick={() => {
        onNavigate?.()
        navigate(to)
      }}
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      {children}
      <ExternalLink className="h-3 w-3" />
    </button>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <h3 className="text-lg font-semibold mt-8 mb-4 first:mt-0">{children}</h3>
}

function SubHeader({ children }: { children: React.ReactNode }) {
  return <h4 className="font-medium mt-6 mb-2">{children}</h4>
}

// Build sections as a function so DocLink can receive onNavigate
function buildSections(onNavigate: () => void): DocSection[] {
  return [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: PlayCircle,
    color: "text-purple-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Welcome to Forge! This guide will help you understand how the platform works and get you creating content quickly.
        </p>

        <SectionHeader>How Forge Works</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Forge is an AI-powered content generation platform. Instead of writing content manually, you set up a system that generates content for you. Here's the basic flow:
        </p>

        <div className="flex flex-wrap items-center gap-2 p-4 bg-muted/50 rounded-lg mb-6">
          <span className="px-3 py-1.5 bg-background rounded font-medium text-sm">Topics</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded font-medium text-sm">Prompts</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded font-medium text-sm">Workflows</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded font-medium text-sm">Generate</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded font-medium text-sm">Articles</span>
        </div>

        <StepList steps={[
          {
            title: "Create Topics",
            description: "Topics are the subjects you want to write about. You can generate them with AI, import from a spreadsheet, or add them manually. Think of topics as your content ideas.",
          },
          {
            title: "Set Up Prompts",
            description: "Prompts are instructions that tell the AI how to write. Each prompt handles one part of your content - like research, writing an outline, drafting the article, or editing. You configure which AI to use, the tone, and specific instructions.",
          },
          {
            title: "Build a Workflow",
            description: "A workflow chains your prompts together into a pipeline. For example: Research → Outline → Draft → Edit. Each step passes its output to the next step, building your content piece by piece.",
          },
          {
            title: "Generate Content",
            description: "Select your topics, choose a workflow, and hit generate. The system processes each topic through your workflow and creates finished articles.",
          },
          {
            title: "Review & Publish",
            description: "Generated content appears in your Articles section. Review it, make any edits, and publish to your WordPress site or export.",
          },
        ]} />

        <Callout type="tip">
          <strong>Quick Start:</strong> Go to <DocLink to="/prompts" onNavigate={onNavigate}>Prompts</DocLink> and use one of the template prompts. Then create a simple workflow with that prompt, and generate your first article!
        </Callout>

        <SectionHeader>Key Concepts</SectionHeader>

        <SubHeader>Topics vs Articles</SubHeader>
        <p className="text-muted-foreground mb-4">
          <strong>Topics</strong> are ideas or subjects - they're inputs. <strong>Articles</strong> are the finished content - they're outputs. One topic becomes one article after going through a workflow.
        </p>

        <SubHeader>Prompts vs Workflows</SubHeader>
        <p className="text-muted-foreground mb-4">
          <strong>Prompts</strong> are individual instructions for a single AI task. <strong>Workflows</strong> chain multiple prompts together. You might have 4 prompts (research, outline, draft, edit) combined into 1 workflow.
        </p>

        <div className="my-4 p-4 bg-muted/50 rounded-lg space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Example: Blog Post Workflow</p>
          <div className="grid grid-cols-4 gap-2">
            <div className="p-3 bg-background rounded-lg border text-center">
              <div className="text-2xl mb-1">🔍</div>
              <div className="text-xs font-medium">Research</div>
              <div className="text-xs text-muted-foreground">Perplexity</div>
            </div>
            <div className="p-3 bg-background rounded-lg border text-center">
              <div className="text-2xl mb-1">📝</div>
              <div className="text-xs font-medium">Outline</div>
              <div className="text-xs text-muted-foreground">Claude</div>
            </div>
            <div className="p-3 bg-background rounded-lg border text-center">
              <div className="text-2xl mb-1">✍️</div>
              <div className="text-xs font-medium">Draft</div>
              <div className="text-xs text-muted-foreground">Claude</div>
            </div>
            <div className="p-3 bg-background rounded-lg border text-center">
              <div className="text-2xl mb-1">✨</div>
              <div className="text-xs font-medium">Polish</div>
              <div className="text-xs text-muted-foreground">GPT-4</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">4 prompts → 1 workflow → each step passes its output to the next</p>
        </div>

        <SubHeader>AI Providers</SubHeader>
        <p className="text-muted-foreground mb-4">
          Forge supports multiple AI providers. Each has strengths:
        </p>
        <ul className="space-y-2 text-sm text-muted-foreground mb-4">
          <li>• <strong>Claude (Anthropic)</strong> - Great for nuanced, well-structured writing</li>
          <li>• <strong>GPT-4 (OpenAI)</strong> - Versatile, good for most tasks</li>
          <li>• <strong>Gemini (Google)</strong> - Good for factual content</li>
          <li>• <strong>Perplexity</strong> - Best for research tasks that need current web data</li>
        </ul>

        <Callout type="info">
          You'll need to add API keys for the providers you want to use. Go to <DocLink to="/settings/integrations" onNavigate={onNavigate}>Settings → Integrations</DocLink> to configure them.
        </Callout>
      </>
    ),
  },
  {
    id: "topics",
    title: "Topics",
    icon: Lightbulb,
    color: "text-yellow-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Topics are the foundation of your content. They represent the subjects, ideas, or titles you want to create content about.
        </p>

        <SectionHeader>What is a Topic?</SectionHeader>
        <p className="text-muted-foreground mb-4">
          A topic is a content idea with associated metadata. At minimum, it has a title (like "How to Start a Garden in Small Spaces"). It can also include:
        </p>
        <FeatureList features={[
          "Category assignment for organization",
          "Content type (blog post, guide, comparison, etc.)",
          "Funnel stage (TOFU, MOFU, BOFU) for marketing alignment",
          "Target keywords for SEO",
          "Notes or additional context",
          "Status (pending, approved, rejected, generated)",
        ]} />

        <SectionHeader>Creating Topics</SectionHeader>

        <Callout type="tip">
          Review topics before approving them. Quality control at this stage saves time later - bad topics make bad articles.
        </Callout>

        <SubHeader>Manual Entry</SubHeader>
        <p className="text-muted-foreground mb-4">
          Click "Add Topic" in the <DocLink to="/topics" onNavigate={onNavigate}>Topics Library</DocLink> to manually enter topics. Good for specific ideas you already have or topics from keyword research.
        </p>

        <SubHeader>Import from Spreadsheet</SubHeader>
        <p className="text-muted-foreground mb-4">
          Have topics in a spreadsheet? Use the import feature to bulk upload. The importer maps your columns to topic fields.
        </p>

        <SectionHeader>Topic Library</SectionHeader>
        <p className="text-muted-foreground mb-4">
          The <DocLink to="/topics" onNavigate={onNavigate}>Topics Library</DocLink> is where all your topics live. From here you can:
        </p>
        <FeatureList features={[
          "Filter by status, category, content type, or funnel stage",
          "Bulk approve or reject topics",
          "Edit topic details",
          "Send approved topics to generation",
          "Track which topics have been generated",
        ]} />

        <SectionHeader>Topic Status Flow</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Topics move through these statuses:
        </p>
        <div className="flex flex-wrap items-center gap-2 p-4 bg-muted/50 rounded-lg mb-4">
          <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded text-sm">Pending</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-2 py-1 bg-green-200 dark:bg-green-900 rounded text-sm">Approved</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-2 py-1 bg-blue-200 dark:bg-blue-900 rounded text-sm">Generated</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Only <strong>Approved</strong> topics can be sent to generation. Once content is created, the topic moves to <strong>Generated</strong>.
        </p>

        <Callout type="warning">
          Topics can also be <strong>Rejected</strong> if you don't want to create content for them. This keeps your library clean without deleting the ideas entirely.
        </Callout>
      </>
    ),
  },
  {
    id: "prompts",
    title: "Prompts",
    icon: FileText,
    color: "text-blue-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Prompts are the instructions you give to AI models. Each prompt defines how one step of your content generation should work.
        </p>

        <SectionHeader>Understanding Prompts</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Think of a prompt as a job description for the AI. It tells the AI:
        </p>
        <FeatureList features={[
          "What task to perform (research, write, edit, etc.)",
          "What tone and style to use",
          "What format the output should be in",
          "Any specific instructions or constraints",
          "What inputs to use (topic, previous outputs, etc.)",
        ]} />

        <SectionHeader>Creating a Prompt</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/prompts" onNavigate={onNavigate}>Prompts</DocLink> and click "Create Prompt" or start with a template.
        </p>

        <SubHeader>Basic Settings</SubHeader>
        <ul className="space-y-3 text-sm text-muted-foreground mb-4">
          <li><strong>Name:</strong> A descriptive name like "Blog Post Draft" or "Research Step"</li>
          <li><strong>Provider:</strong> Which AI to use (Claude, GPT-4, Gemini, Perplexity)</li>
          <li><strong>Model:</strong> The specific model version (e.g., claude-3-opus, gpt-4-turbo)</li>
          <li><strong>Temperature:</strong> Creativity level. Lower (0.3) = more focused, Higher (0.9) = more creative</li>
        </ul>

        <SubHeader>The Prompt Text</SubHeader>
        <p className="text-muted-foreground mb-4">
          This is where you write your instructions. You can use variables that get replaced when the prompt runs:
        </p>
        <div className="bg-muted p-4 rounded-lg font-mono text-sm mb-4">
          <p className="text-muted-foreground mb-2"># Example prompt:</p>
          <p>Write a comprehensive blog post about: <span className="text-primary">{"{{topic}}"}</span></p>
          <p className="mt-2">Use these research notes:</p>
          <p><span className="text-primary">{"{{previous_output}}"}</span></p>
          <p className="mt-2">Target audience: Small business owners</p>
          <p>Tone: Professional but approachable</p>
          <p>Length: 1500-2000 words</p>
        </div>

        <SubHeader>Available Variables</SubHeader>
        <ul className="space-y-2 text-sm text-muted-foreground mb-4">
          <li><code className="bg-muted px-1.5 py-0.5 rounded">{"{{topic}}"}</code> - The topic title being processed</li>
          <li><code className="bg-muted px-1.5 py-0.5 rounded">{"{{previous_output}}"}</code> - Output from the previous workflow step</li>
          <li><code className="bg-muted px-1.5 py-0.5 rounded">{"{{category}}"}</code> - The topic's category</li>
          <li><code className="bg-muted px-1.5 py-0.5 rounded">{"{{keywords}}"}</code> - Target keywords for the topic</li>
        </ul>

        <Callout type="tip">
          <strong>Use Perplexity for research steps.</strong> Unlike other models, Perplexity can search the web for current information. Great for fact-checking, finding statistics, or researching trends.
        </Callout>

        <SectionHeader>Prompt Templates</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Templates are pre-built prompts you can use as starting points. They're configured with best practices for common tasks:
        </p>
        <FeatureList features={[
          "Research prompts that gather information",
          "Outline prompts that structure content",
          "Draft prompts that write full articles",
          "Edit prompts that polish and improve content",
        ]} />

        <Callout type="info">
          Templates are just starting points. Customize them for your niche, tone, and specific needs.
        </Callout>

        <SectionHeader>Testing Prompts</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Before adding a prompt to a workflow, test it individually:
        </p>
        <StepList steps={[
          {
            title: "Open the prompt",
            description: "Click on a prompt to open its details.",
          },
          {
            title: "Use the test feature",
            description: "Enter a sample topic and run the prompt to see the output.",
          },
          {
            title: "Refine as needed",
            description: "Adjust instructions, temperature, or model based on results.",
          },
        ]} />
      </>
    ),
  },
  {
    id: "workflows",
    title: "Workflows",
    icon: Workflow,
    color: "text-green-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Workflows chain multiple prompts together into a content generation pipeline. Each step's output feeds into the next step.
        </p>

        <SectionHeader>How Workflows Work</SectionHeader>
        <p className="text-muted-foreground mb-4">
          A workflow is a sequence of prompts that run in order. For example:
        </p>
        <div className="flex flex-wrap items-center gap-2 p-4 bg-muted/50 rounded-lg mb-4">
          <span className="px-3 py-1.5 bg-background rounded text-sm">Step 1: Research</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded text-sm">Step 2: Outline</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded text-sm">Step 3: Draft</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="px-3 py-1.5 bg-background rounded text-sm">Step 4: Edit</span>
        </div>
        <p className="text-muted-foreground mb-4">
          When you run a topic through this workflow:
        </p>
        <ol className="space-y-2 text-sm text-muted-foreground mb-4 list-decimal list-inside">
          <li>The Research prompt gets the topic and produces research notes</li>
          <li>The Outline prompt gets the topic + research notes and produces an outline</li>
          <li>The Draft prompt gets the topic + outline and writes the article</li>
          <li>The Edit prompt gets the draft and produces the final polished article</li>
        </ol>

        <SectionHeader>Creating a Workflow</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/workflows" onNavigate={onNavigate}>Workflows</DocLink> and click "Create Workflow".
        </p>
        <StepList steps={[
          {
            title: "Name your workflow",
            description: "Use a descriptive name like 'Blog Post - Full Process' or 'Quick Listicle'.",
          },
          {
            title: "Add steps",
            description: "Click 'Add Step' and select a prompt. Each step uses one of your saved prompts.",
          },
          {
            title: "Arrange the order",
            description: "Drag steps to reorder them. The order matters - outputs flow from one step to the next.",
          },
          {
            title: "Configure step settings",
            description: "Each step can have settings like whether to use previous output, add specific context, etc.",
          },
          {
            title: "Save and test",
            description: "Save your workflow, then test it with a single topic before batch processing.",
          },
        ]} />

        <Callout type="warning">
          <strong>You need prompts first.</strong> You can't create a workflow without having at least one prompt saved. Create your prompts before building workflows.
        </Callout>

        <SectionHeader>Workflow Patterns</SectionHeader>

        <SubHeader>Simple (1-2 steps)</SubHeader>
        <p className="text-muted-foreground mb-4">
          Good for quick content. Example: Just a Draft prompt that writes the whole article.
        </p>

        <SubHeader>Standard (3-4 steps)</SubHeader>
        <p className="text-muted-foreground mb-4">
          Best for most content. Example: Research → Outline → Draft → Edit
        </p>

        <SubHeader>Comprehensive (5+ steps)</SubHeader>
        <p className="text-muted-foreground mb-4">
          For high-quality, detailed content. Example: Research → Competitor Analysis → Outline → Draft → Fact Check → SEO Optimization → Final Edit
        </p>

        <Callout type="tip">
          <strong>Start simple.</strong> Begin with a 2-3 step workflow and add complexity as needed. It's easier to add steps than to debug a complex workflow.
        </Callout>

        <SectionHeader>Tips for Good Workflows</SectionHeader>
        <FeatureList features={[
          "Use Perplexity for research steps that need web data",
          "Keep each step focused on one task",
          "Use Claude or GPT-4 for writing steps",
          "Always end with an edit/polish step for quality",
          "Test with one topic before running batches",
        ]} />
      </>
    ),
  },
  {
    id: "articles",
    title: "Articles",
    icon: FileText,
    color: "text-slate-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          The Articles section is where your generated content lives. Here you can review, edit, and manage all your content.
        </p>

        <SectionHeader>Viewing Articles</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/articles" onNavigate={onNavigate}>Articles</DocLink> to see all your content. You can:
        </p>
        <FeatureList features={[
          "Filter by status (draft, ready, published)",
          "Search by title or content",
          "Sort by date, title, or status",
          "Bulk select for actions",
        ]} />

        <SectionHeader>Article Status</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Articles move through these statuses:
        </p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded text-sm w-24 text-center">Draft</span>
            <span className="text-sm text-muted-foreground">Just generated, needs review</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 bg-blue-200 dark:bg-blue-900 rounded text-sm w-24 text-center">Ready</span>
            <span className="text-sm text-muted-foreground">Reviewed and ready to publish</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 bg-green-200 dark:bg-green-900 rounded text-sm w-24 text-center">Published</span>
            <span className="text-sm text-muted-foreground">Live on your website</span>
          </div>
        </div>

        <SectionHeader>The Content Editor</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Click on any article to open the full-featured content editor. It's designed for both reviewing AI-generated content and making your own edits.
        </p>

        <SubHeader>Editor Features</SubHeader>
        <FeatureList features={[
          "Rich text editing with full formatting (bold, italic, headings, lists, links)",
          "SEO fields (meta title, description, slug)",
          "Category and tag assignment",
          "Featured image upload and AI image generation",
          "Version history to see and restore previous versions",
          "Preview before publishing",
        ]} />

        <SubHeader>AI Writing Assistance</SubHeader>
        <p className="text-muted-foreground mb-4">
          The editor includes AI-powered tools to help you write and improve content:
        </p>
        <FeatureList features={[
          "Slash commands (/) - Type / to see available AI actions",
          "Continue writing - AI continues from where you left off",
          "Improve selection - Highlight text and ask AI to improve it",
          "Expand or shorten - Make sections longer or more concise",
          "Fix grammar and spelling - Clean up your writing",
          "Change tone - Make content more formal, casual, etc.",
        ]} />

        <Callout type="tip">
          <strong>Keyboard shortcut:</strong> Press <code className="bg-muted px-1 rounded">/</code> anywhere in the editor to open the AI command menu.
        </Callout>

        <SubHeader>Link Suggestions</SubHeader>
        <p className="text-muted-foreground mb-4">
          The editor can suggest internal links to your other content based on AI-powered similarity matching:
        </p>
        <FeatureList features={[
          "Panel shows related articles ranked by relevance",
          "Click to copy link or insert at cursor position",
          "Powered by content embeddings (see Similarity & Linking section)",
          "Helps with SEO and keeps readers on your site",
        ]} />

        <SubHeader>Version History</SubHeader>
        <p className="text-muted-foreground mb-4">
          Every save creates a version you can restore later. Click the history icon to:
        </p>
        <FeatureList features={[
          "See all previous versions with timestamps",
          "Compare changes between versions",
          "Restore any previous version instantly",
        ]} />

        <SectionHeader>Publishing to WordPress</SectionHeader>
        <p className="text-muted-foreground mb-4">
          If you've connected WordPress in settings:
        </p>
        <StepList steps={[
          {
            title: "Open the article",
            description: "Click on the article you want to publish.",
          },
          {
            title: "Review and mark ready",
            description: "Make any edits, then change status to 'Ready'.",
          },
          {
            title: "Publish",
            description: "Click 'Publish to WordPress' to send it to your site.",
          },
        ]} />

        <Callout type="info">
          Scheduled and recently published content is listed in the publishing schedule at the foot of <DocLink to="/autopilot#schedule" onNavigate={onNavigate}>Autopilot</DocLink>.
        </Callout>
      </>
    ),
  },
  {
    id: "newsroom",
    title: "Newsroom",
    icon: Newspaper,
    color: "text-orange-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Newsroom drafts news articles from a site's beat and holds every one of them for a
          person to read before it publishes. There is no auto-publish setting, and no flag that
          turns one on.
        </p>

        <SectionHeader>Reviewing a draft</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Open <DocLink to="/newsroom" onNavigate={onNavigate}>Newsroom</DocLink>. The queue opens
          on Needs Review.
        </p>
        <FeatureList features={[
          "The source article and the draft sit side by side — read them against each other",
          "Primary documents are a checklist: the filing or order is the evidence and the legal defence",
          "Automated checks are listed in full, passes included, so you can see what was verified",
          "j and k move between items; approving is click-only, on purpose",
          "Approve names the site it publishes to and the byline it will carry",
        ]} />

        <Callout type="warning">
          <strong>Approving publishes immediately.</strong> There is no staging step between the
          confirmation and a live article on the client's site. Tier B and above are flagged in
          the queue and again on the confirmation — those are the ones worth slowing down for.
        </Callout>

        <SectionHeader>After publishing</SectionHeader>
        <FeatureList features={[
          "Take Down unpublishes and redacts an article that should not have gone out",
          "Issue Correction appends a correction and leaves the article up",
          "Every decision is recorded against the item with who made it and why",
        ]} />

        <SectionHeader>Settings</SectionHeader>
        <p className="text-muted-foreground mb-4">
          In <DocLink to="/settings/newsroom" onNavigate={onNavigate}>Settings → Newsroom</DocLink>:
        </p>
        <FeatureList features={[
          "Enable or disable drafting for the site",
          "The beat — one narrow subject, which is what earns topical authority",
          "Byline name and URL, shown on the approve confirmation",
          "Vertical is set by an admin and is read-only",
        ]} />

        <Callout type="info">
          <strong>Feeds:</strong> configured in rss.app, pointing at Forge's webhook. They are not
          editable in Forge.
        </Callout>
      </>
    ),
  },
  {
    id: "cms",
    title: "CMS Integration",
    icon: Globe,
    color: "text-teal-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Connect your WordPress site to sync content, manage posts, and publish directly from Forge.
        </p>

        <SectionHeader>Connecting WordPress</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/settings/websites" onNavigate={onNavigate}>Settings → Websites</DocLink> to connect your WordPress site.
        </p>
        <StepList steps={[
          {
            title: "Get your site URL",
            description: "Your WordPress site URL (e.g., https://yoursite.com)",
          },
          {
            title: "Create Application Password",
            description: "In WordPress, go to Users → Your Profile → Application Passwords. Create a new password for Forge.",
          },
          {
            title: "Enter credentials in Forge",
            description: "Add your site URL, username, and the application password.",
          },
          {
            title: "Test connection",
            description: "Click test to verify Forge can connect to your site.",
          },
        ]} />

        <Callout type="warning">
          <strong>Application Passwords Required:</strong> You must use WordPress Application Passwords, not your regular login password. This is more secure and can be revoked if needed.
        </Callout>

        <SectionHeader>CMS Features</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Once connected, <DocLink to="/settings/websites" onNavigate={onNavigate}>Settings &gt; Websites</DocLink> lets you:
        </p>
        <FeatureList features={[
          "View all WordPress posts and pages",
          "Sync content between Forge and WordPress",
          "Manage categories and tags",
          "Upload and manage media",
          "Bulk publish or update content",
        ]} />

        <SectionHeader>Syncing Content</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Forge can sync in both directions:
        </p>
        <ul className="space-y-2 text-sm text-muted-foreground mb-4">
          <li><strong>Push to WordPress:</strong> Send Forge articles to your WordPress site</li>
          <li><strong>Pull from WordPress:</strong> Import existing WordPress content into Forge</li>
        </ul>

        <Callout type="tip">
          Sync regularly if you make edits in both places. The most recent edit wins during sync.
        </Callout>
      </>
    ),
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    color: "text-slate-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Configure your Forge account, connect services, and customize how the platform works.
        </p>

        <SectionHeader>Settings Sections</SectionHeader>

        <SubHeader>License</SubHeader>
        <p className="text-muted-foreground mb-4">
          View and manage your Forge license. See which features are included and your usage limits.
        </p>

        <SubHeader>Websites</SubHeader>
        <p className="text-muted-foreground mb-4">
          Connect your WordPress sites. You can have multiple sites connected and switch between them.
        </p>

        <SubHeader>Integrations</SubHeader>
        <p className="text-muted-foreground mb-4">
          Add API keys for AI providers and other services:
        </p>
        <FeatureList features={[
          "Claude (Anthropic) API key",
          "OpenAI API key",
          "Google Gemini API key",
          "Perplexity API key",
          "Jina Reader API key (article text extraction)",
        ]} />

        <Callout type="info">
          <strong>API Keys Required:</strong> You need at least one AI provider key to generate content. We recommend starting with Claude or OpenAI.
        </Callout>

        <SubHeader>Usage</SubHeader>
        <p className="text-muted-foreground mb-4">
          Track your API usage, tokens consumed, and costs across providers.
        </p>

        <SubHeader>Analytics</SubHeader>
        <p className="text-muted-foreground mb-4">
          Connect Google Search Console and Analytics to track content performance.
        </p>

        <SubHeader>Account</SubHeader>
        <p className="text-muted-foreground mb-4">
          Manage your account settings, change password, and configure preferences.
        </p>
      </>
    ),
  },
  {
    id: "similarity-linking",
    title: "Similarity & Linking",
    icon: Link2,
    color: "text-cyan-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Forge uses AI embeddings to understand your content, detect duplicates, and suggest relevant internal links.
        </p>

        <SectionHeader>How Embeddings Work</SectionHeader>
        <p className="text-muted-foreground mb-4">
          When you create content, Forge generates an "embedding" - a mathematical representation of what the content is about. This powers several features:
        </p>
        <FeatureList features={[
          "Duplicate detection - find content that's too similar",
          "Link suggestions - find related content to link to",
          "Content clustering - group similar topics together",
        ]} />

        <div className="my-4 p-4 bg-muted/50 rounded-lg">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">How it works</p>
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-background rounded border">Your Article</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="px-2 py-1 bg-background rounded border">AI Embedding</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="px-2 py-1 bg-background rounded border">Compare to Library</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900 rounded border border-cyan-300">Similar Content</span>
          </div>
        </div>

        <SectionHeader>Duplicate Detection</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Forge automatically checks new content against your existing library to prevent duplicates:
        </p>
        <FeatureList features={[
          "Topics can be checked for overlap with existing content",
          "Similarity threshold is configurable in Settings → Similarity",
        ]} />

        <Callout type="info">
          A similarity score of 85%+ typically indicates duplicate content. Scores of 60-85% suggest related but distinct content.
        </Callout>

        <SectionHeader>Internal Link Suggestions</SectionHeader>
        <p className="text-muted-foreground mb-4">
          The system suggests internal links based on content similarity. This appears in two places:
        </p>

        <SubHeader>In the Content Editor</SubHeader>
        <p className="text-muted-foreground mb-4">
          When editing an article, the Link Suggestions panel shows:
        </p>
        <FeatureList features={[
          "Related articles from your library ranked by relevance",
          "Click to copy the link or insert at cursor",
          "Helps with SEO and keeps readers on your site",
        ]} />

        <SectionHeader>Configuring Similarity</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/settings/similarity" onNavigate={onNavigate}>Settings → Similarity</DocLink> to configure:
        </p>
        <FeatureList features={[
          "Similarity threshold for duplicate detection",
          "Which content types to include in comparisons",
          "Whether to block or warn on duplicates",
        ]} />

        <Callout type="tip">
          <strong>Best practice:</strong> Keep the duplicate threshold around 80-85%. Too low and you'll get false positives; too high and duplicates slip through.
        </Callout>
      </>
    ),
  },
  {
    id: "seo-tools",
    title: "SEO Tools",
    icon: Search,
    color: "text-red-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Forge includes several SEO tools to help you plan content strategy and optimize for search engines.
        </p>

        <SectionHeader>Keyword Research</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/keywords" onNavigate={onNavigate}>Keywords</DocLink> to research keywords for your content.
        </p>
        <FeatureList features={[
          "Search volume data",
          "Keyword difficulty scores",
          "Related keyword suggestions",
          "SERP analysis",
          "Convert keywords to topics",
        ]} />

        <Callout type="tip">
          Focus on long-tail keywords (3+ words) for easier ranking, especially for newer sites.
        </Callout>

      </>
    ),
  },
  {
    id: "autopilot",
    title: "Autopilot",
    icon: Bot,
    color: "text-amber-500",
    content: (
      <>
        <p className="text-muted-foreground mb-6">
          Autopilot generates and publishes blog articles on a schedule — fully automatic. This guide walks through every step from a blank site to a running autopilot.
        </p>

        <SectionHeader>End-to-End Setup Guide</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Follow these steps in order. If you skip one, autopilot won't work.
        </p>

        <SubHeader>Step 1: Connect WordPress</SubHeader>
        <p className="text-muted-foreground mb-4">
          Autopilot needs a WordPress site to publish to. Go to <DocLink to="/settings/websites" onNavigate={onNavigate}>Settings &gt; Websites</DocLink> and connect your WordPress site using the Forge plugin.
        </p>
        <StepList steps={[
          {
            title: "Install the Forge plugin on WordPress",
            description: "Download from the CMS settings page and upload to WordPress via Plugins > Add New > Upload Plugin.",
          },
          {
            title: "Activate and copy the connection key",
            description: "After activating, go to the Forge Plugin settings in your WordPress admin. Copy the connection key shown there.",
          },
          {
            title: "Paste the key in Forge",
            description: "Back in Forge CMS settings, paste the key and click Connect. The status should turn green.",
          },
        ]} />
        <Callout type="warning">
          If the connection says "disconnected" later, the plugin may have been deactivated on WordPress, or the site may be down. Check WP Admin &gt; Plugins first.
        </Callout>

        <SubHeader>Step 2: Fill Out Client Info</SubHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/settings/profile" onNavigate={onNavigate}>Settings &gt; Client Info</DocLink>. Enter the website URL and click <strong>AI Autofill</strong> to automatically populate the business name, industry, services, and voice settings. Review and save. This context is used by the AI when generating content — skip it and articles will be generic.
        </p>

        <SubHeader>Step 3: Set Up Prompts</SubHeader>
        <p className="text-muted-foreground mb-4">
          You need at least one prompt that tells the AI how to write. Go to <DocLink to="/prompts" onNavigate={onNavigate}>Prompts</DocLink> and create two prompts:
        </p>
        <StepList steps={[
          {
            title: "Research prompt (optional but recommended)",
            description: "Provider: Perplexity (sonar-pro). This gathers current info about the topic before writing. Set the system prompt to describe the research focus area and list preferred sources.",
          },
          {
            title: "Writing prompt",
            description: "Provider: Anthropic (Claude Sonnet). This is the main article writer. Set the system prompt with voice/tone, banned words, formatting rules. Set the user prompt with article structure requirements. Use {{blog_topic_and_title}} for the topic and {{research}} for the research output.",
          },
        ]} />
        <Callout type="tip">
          Copy an existing site's prompts as a starting point — go to a site that's already set up (like Eric Goldman Law), view the prompts, and adapt them for the new client's voice and industry.
        </Callout>

        <SubHeader>Step 4: Build a Workflow</SubHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/workflows" onNavigate={onNavigate}>Workflows</DocLink> and create a workflow that chains your prompts together.
        </p>
        <StepList steps={[
          {
            title: "Create the workflow",
            description: "Give it a clear name like '[Client] Blog Writer'. Click Add Step.",
          },
          {
            title: "Add the Research step (Step 1)",
            description: "Select your Research prompt. Map the input variable 'blog_topic_and_title' to 'workflow.topic'. This feeds the topic title into the research prompt.",
          },
          {
            title: "Add the Writing step (Step 2)",
            description: "Select your Writing prompt. Map 'blog_topic_and_title' to 'workflow.topic' and 'research' to 'step_0.output'. This feeds both the topic and the research results into the writing prompt.",
          },
          {
            title: "Save and test",
            description: "Save the workflow. You can test it by going to Topics, creating a test topic, and running the workflow on it manually before enabling autopilot.",
          },
        ]} />

        <SubHeader>Step 5: Add Topics</SubHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/topics" onNavigate={onNavigate}>Topics Library</DocLink>. Autopilot needs approved topics to write about. You can add them in two ways:
        </p>
        <FeatureList features={[
          "Manual — Click 'Add Topic' and type a title and category",
          "CSV Import — Upload a spreadsheet with topic titles and categories",
        ]} />
        <p className="text-muted-foreground mb-4 mt-4">
          After adding topics, you need to <strong>approve</strong> them. Select all the ones you want (checkbox + Select All), then click the Approve button in the bulk actions bar. Autopilot only uses approved topics.
        </p>
        <Callout type="warning">
          Autopilot will pause itself when the approved topic queue gets low (under 5 by default). Keep the queue stocked with at least 20-30 approved topics to avoid interruptions.
        </Callout>

        <SubHeader>Step 6: Configure Autopilot</SubHeader>
        <p className="text-muted-foreground mb-4">
          Go to <DocLink to="/autopilot" onNavigate={onNavigate}>Autopilot</DocLink> and click <strong>Configure</strong>.
        </p>
        <StepList steps={[
          {
            title: "Select the workflow",
            description: "Choose the workflow you built in Step 4. This is the pipeline autopilot will run for every article.",
          },
          {
            title: "Set articles per day",
            description: "Start with 1-2 per day. You can increase later. More than 3/day can look suspicious to Google for new sites.",
          },
          {
            title: "Set the schedule",
            description: "Choose which days to publish (Mon-Fri is typical — skip weekends). Set the time window (e.g., 9am-5pm). Autopilot spaces articles evenly within the window.",
          },
          {
            title: "Set WordPress publishing defaults",
            description: "Choose: Production or Staging connection, Post type (usually 'post'), Default category, Author (pick the client's WP user), Post status ('publish' for live, 'draft' to review first).",
          },
          {
            title: "Enable auto-approve and auto-publish",
            description: "For hands-off operation, turn on both 'Auto-approve content' and 'Auto-publish to WordPress'. For supervised mode, leave these off and review each article manually.",
          },
        ]} />

        <SubHeader>Step 7: Enable and Monitor</SubHeader>
        <p className="text-muted-foreground mb-4">
          Click <strong>Enable</strong> on the Autopilot Dashboard. Then click <strong>Force Run</strong> to trigger the first article immediately so you can verify everything works. Check:
        </p>
        <FeatureList features={[
          "Does the article generate without errors? (Check the activity log)",
          "Does it publish to WordPress? (Check the Published tab)",
          "Does the content quality look good? (Click the external link to view on the live site)",
          "Is the category, author, and post status correct in WordPress?",
        ]} />
        <Callout type="tip">
          Always Force Run the first article and check it on the live site before walking away. Catching issues early saves you from publishing 10 bad articles overnight.
        </Callout>

        <SectionHeader>Automation Levels</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Three modes, from most supervised to most hands-off:
        </p>
        <FeatureList features={[
          "Generate only — Articles are created but saved as drafts. You review each one in Forge and publish manually. Best for new setups while you're dialing in quality.",
          "Auto-approve + manual publish — Articles are generated and approved, but not pushed to WordPress. You click publish when ready. Good middle ground.",
          "Full auto — Articles are generated, approved, and published to WordPress live. No manual steps. Best for sites where you've validated the quality.",
        ]} />

        <SectionHeader>When Things Go Wrong</SectionHeader>

        <SubHeader>Autopilot paused itself</SubHeader>
        <p className="text-muted-foreground mb-4">
          Check the dashboard — it shows why. Common reasons: topic queue ran out (add more approved topics), too many consecutive failures (check the error messages in the activity log), or it was manually paused. Fix the issue and click Resume.
        </p>

        <SubHeader>Articles are failing to generate</SubHeader>
        <p className="text-muted-foreground mb-4">
          Check the activity log for error messages. Common causes: API key expired or out of credits (check <DocLink to="/settings/integrations" onNavigate={onNavigate}>Settings &gt; Integrations</DocLink>), the Perplexity key is invalid, or the prompt has a template variable that doesn't match the workflow mapping.
        </p>

        <SubHeader>Articles generate but don't publish to WordPress</SubHeader>
        <p className="text-muted-foreground mb-4">
          Check that auto-publish is turned on in Configure. Check the WordPress connection in <DocLink to="/settings/websites" onNavigate={onNavigate}>Settings &gt; Websites</DocLink> — if it says disconnected, the plugin may need to be reactivated. Also check that the WP user has permission to create posts.
        </p>

        <SubHeader>Content quality is bad</SubHeader>
        <p className="text-muted-foreground mb-4">
          The problem is almost always in the prompts, not autopilot itself. Pause autopilot, run a topic manually through the workflow, read the output, and adjust the prompts. Common fixes: add more specific voice instructions, add a banned words list, add writing examples, increase the temperature slightly (0.7-0.8).
        </p>

        <SectionHeader>Email Notifications</SectionHeader>
        <p className="text-muted-foreground mb-4">
          Configure in the Autopilot settings to stay informed:
        </p>
        <FeatureList features={[
          "Per article — Email every time an article is published with title, excerpt, and link",
          "Daily digest — One summary email per day with counts of generated, published, and failed articles",
          "Both — Per-article emails plus a daily digest",
        ]} />
        <Callout type="warning">
          Email notifications require recipients to be configured in <DocLink to="/settings" onNavigate={onNavigate}>Settings &gt; Notifications</DocLink> with the "Autopilot Summary" toggle enabled.
        </Callout>

        <SectionHeader>Quick Reference</SectionHeader>
        <p className="text-muted-foreground mb-2">
          Minimum requirements to run autopilot:
        </p>
        <FeatureList features={[
          "WordPress connected and working (green status in CMS settings)",
          "Client Info filled out (at minimum: website URL and business name)",
          "At least one prompt configured",
          "A workflow with at least one step",
          "10+ approved topics in the queue",
          "Workflow selected in autopilot Configure",
          "Autopilot enabled",
        ]} />
      </>
    ),
  },
]
}

interface DocsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultSection: string
}

export function DocsModal({ open, onOpenChange, defaultSection }: DocsModalProps) {
  const [activeSection, setActiveSection] = useState(defaultSection)
  const contentRef = useRef<HTMLDivElement>(null)

  // When defaultSection changes while opening, jump to that section
  useEffect(() => {
    if (open) {
      setActiveSection(defaultSection)
    }
  }, [defaultSection, open])

  // Scroll content to top when section changes
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0)
  }, [activeSection])

  const handleClose = () => onOpenChange(false)

  const sections = buildSections(handleClose)
  const currentSection = sections.find(s => s.id === activeSection)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl !flex !flex-row h-[80vh] p-0 gap-0">
        <VisuallyHidden>
          <DialogTitle>Help Center</DialogTitle>
        </VisuallyHidden>
        {/* Sidebar */}
        <div className="w-64 border-r bg-muted/30 flex-shrink-0 flex flex-col overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="font-semibold flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Help Center
            </h2>
            <p className="text-xs text-muted-foreground mt-1">Learn how Forge works</p>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors",
                  activeSection === section.id
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                <section.icon className={cn("h-4 w-4", activeSection === section.id ? section.color : "")} />
                <span className="truncate">{section.title}</span>
                {activeSection === section.id && (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-8">
            {currentSection && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className={cn("p-2 rounded-lg", currentSection.color.replace("text-", "bg-") + "/10")}>
                    <currentSection.icon className={cn("h-6 w-6", currentSection.color)} />
                  </div>
                  <h2 className="text-2xl font-bold">{currentSection.title}</h2>
                </div>
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  {currentSection.content}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
